import { authSplitApi } from 'redux/helpers/slice.helpers';
import { exportCsvConverter } from 'data/convertors/general.converters';
import { TExportCsvDB, TExportCsvUI } from 'data/types/generic.types';
import { downloadFile } from 'redux/services/fileServices';

export interface KitInventoryItem {
  resourceId: string;
  name: string;
  ageGroup: string;
  language: string;
  inventory: number;
  lastReceivedDate: string | null;
}

export type TWorkshopPartKitNoteUI = {
  noteId: string | null;
  note: string;
};

export type TWorkshopPartKitNotesResponse = {
  noteId: string;
  note: string;
  createdAt: string;
};

export type TOrganizationKitNoteUI = {
  noteId: number | null;
  text: string;
};

export type TOrganizationKitNotesResponse = {
  noteId: number;
  note: string;
  date: string;
};

export type TOrganizationKitOrderHistoryItem = {
  workshopKitId: string;
  kitName: string;
  dateModified: string;
  operation: string;
  quantityChange: number;
};

export type TOrganizationKitOrderHistoryResponse = {
  inventoryHistory: {
    [date: string]: TOrganizationKitOrderHistoryItem[];
  };
};

export interface OrganizationKitInventoryResponse {
  page: {
    content: KitInventoryItem[];
    size: number;
    number: number;
    totalPages: number;
    totalElements: number;
  };
  filters: {
    language: Record<string, string>;
    ageGroup: Record<string, string>;
    kits: Record<string, string>;
  };
}

export interface KitInventoryParams {
  organizationId: string;
  pageSize?: number;
  page?: number;
  sortField?: string;
  sortDirection?: string;
  resourceIds?: string;
  language?: string;
  ageGroup?: string;
}

export type WorkshopKitCategory =
  | 'RFK_PART_ONE'
  | 'RFK_PART_TWO'
  | 'RFK_PART_THREE'
  | 'SEL_MATH'
  | 'CCP';

export interface KitInventoryByCategoryParams extends KitInventoryParams {
  category: WorkshopKitCategory;
}

export interface UpdateOrganizationKitInventoryParams {
  inventoryId: string;
  inventory: number;
}

export interface UpdateOrganizationKitInventoryResponse {
  resourceId: string;
  inventory: number;
  lastReceivedDate: string;
}

export const organizationKitInventoryApi = authSplitApi(
  'organizationKitInventory',
  ['organization-kit-inventory', 'organization-kit-inventory-notes'],
).injectEndpoints({
  endpoints: build => ({
    getOrganizationKitInventory: build.query<
      OrganizationKitInventoryResponse,
      KitInventoryParams
    >({
      query: params => {
        const queryParams: Record<string, any> = {
          pageSize: params.pageSize,
          page: params.page,
        };

        if (params.resourceIds) queryParams.resourceIds = params.resourceIds;
        if (params.language) queryParams.language = params.language;
        if (params.ageGroup) queryParams.ageGroup = params.ageGroup;

        if (params.sortField) queryParams.sortField = params.sortField;
        if (params.sortDirection)
          queryParams.sortDirection = params.sortDirection;

        return {
          url: `/ordering/organizations/kit-inventory/${params.organizationId}`,
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['organization-kit-inventory'],
    }),
    getOrganizationKitInventoryByCategory: build.query<
      OrganizationKitInventoryResponse,
      KitInventoryByCategoryParams
    >({
      query: params => {
        const queryParams: Record<string, any> = {
          pageSize: params.pageSize,
          page: params.page,
        };

        if (params.resourceIds) queryParams.resourceIds = params.resourceIds;
        if (params.language) queryParams.language = params.language;
        if (params.ageGroup) queryParams.ageGroup = params.ageGroup;

        if (params.sortField) queryParams.sortField = params.sortField;
        if (params.sortDirection)
          queryParams.sortDirection = params.sortDirection;

        return {
          url: `/ordering/organizations/kit-inventory/${params.organizationId}/${params.category}`,
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['organization-kit-inventory'],
    }),
    updateOrganizationKitInventory: build.mutation<
      UpdateOrganizationKitInventoryResponse,
      UpdateOrganizationKitInventoryParams
    >({
      query: params => ({
        url: `/ordering/organizations/kit-inventory/${params.inventoryId}`,
        method: 'PUT',
        body: {
          quantity: params.inventory,
        },
      }),
      invalidatesTags: ['organization-kit-inventory'],
    }),
    getWorkshopPartKitNotes: build.query<
      TOrganizationKitNotesResponse[],
      { workshopId: string; partId: string }
    >({
      query({ workshopId }) {
        return {
          url: `/ordering/organizations/kit-inventory/${workshopId}/notes`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { workshopId }) => [
        { type: 'organization-kit-inventory-notes', id: workshopId },
      ],
    }),
    updateWorkshopPartKitNotes: build.mutation<
      void,
      { workshopId: string; partId: string; notes: TOrganizationKitNoteUI[] }
    >({
      query({ workshopId, notes }) {
        return {
          url: `/ordering/organizations/kit-inventory/${workshopId}/notes`,
          method: 'POST',
          body: notes,
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'organization-kit-inventory-notes', id: workshopId },
      ],
    }),
    deleteWorkshopPartKitNote: build.mutation<
      void,
      { workshopId: string; partId: string; noteId: string }
    >({
      query({ noteId }) {
        return {
          url: `/ordering/organizations/kit-inventory/notes/${noteId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'organization-kit-inventory-notes', id: workshopId },
      ],
    }),
    getEventPartKitNotes: build.query<
      TOrganizationKitNotesResponse[],
      { eventId: string; partId: string }
    >({
      query({ eventId }) {
        return {
          url: `/ordering/organizations/kit-inventory/${eventId}/notes`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { eventId }) => [
        { type: 'organization-kit-inventory-notes', id: eventId },
      ],
    }),
    updateEventPartKitNotes: build.mutation<
      void,
      { eventId: string; partId: string; notes: TOrganizationKitNoteUI[] }
    >({
      query({ eventId, notes }) {
        return {
          url: `/ordering/organizations/kit-inventory/${eventId}/notes`,
          method: 'POST',
          body: notes,
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'organization-kit-inventory-notes', id: eventId },
      ],
    }),
    deleteEventPartKitNote: build.mutation<
      void,
      { eventId: string; partId: string; noteId: string }
    >({
      query({ noteId }) {
        return {
          url: `/ordering/organizations/kit-inventory/notes/${noteId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'organization-kit-inventory-notes', id: eventId },
      ],
    }),
    getOrganizationKitOrderHistory: build.query<
      TOrganizationKitOrderHistoryResponse,
      string
    >({
      query(organizationId) {
        return {
          url: `/ordering/organizations/kit-inventory/${organizationId}/history`,
          method: 'GET',
        };
      },
    }),
    exportOrganizationKitInventory: build.mutation<
      TExportCsvUI,
      {
        organizationId: string;
        columns: string[];
        filters: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `/ordering/organizations/kit-inventory/export`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
  }),
});

export const {
  useGetOrganizationKitInventoryQuery,
  useGetOrganizationKitInventoryByCategoryQuery,
  useUpdateOrganizationKitInventoryMutation,
  useGetWorkshopPartKitNotesQuery,
  useUpdateWorkshopPartKitNotesMutation,
  useDeleteWorkshopPartKitNoteMutation,
  useGetEventPartKitNotesQuery,
  useUpdateEventPartKitNotesMutation,
  useDeleteEventPartKitNoteMutation,
  useGetOrganizationKitOrderHistoryQuery,
  useExportOrganizationKitInventoryMutation,
} = organizationKitInventoryApi;
